import { useTranslation } from 'react-i18next';
import { unitLabel } from '@d-orca/shared/client/logistics';
import { useNow } from '@hooks/useNow';
import type { UntrackedMaterial } from '@interfaces/ILogistics';
import { zonedDay } from '../logistics.format';
import { useLogisticsEditLock } from '../revision/LogisticsEditLock';
import { InfoGlyph, JumpChevron, TruckGlyph } from './InventoryRowGlyphs';
import { untrackedDue, untrackedOnSite } from '../stockActions';
import styles from './InventoryLevels.module.css';

interface IInventoryUntrackedRowProps {
  material: UntrackedMaterial;
  /** The PROJECT's IANA timezone — "today" for the due-date check is measured
   *  against it. */
  tz: string;
  /** A line that NAMES a delivery offers to go to it. The panel does not own
   *  the pipeline, so it raises the ref and the page routes it. */
  onJumpToDelivery: (deliveryRef: string) => void;
  /** Record stock for this material: opens the stock dialog seeded from the
   *  delivery, so all that is left to type is how long it should last. */
  onAddStock: (material: UntrackedMaterial) => void;
  /** The caller's role may write: without it Add stock is not rendered; the
   *  jump to the order stays live. */
  canEdit: boolean;
}

/**
 * A material that exists on the delivery side only, above the tracked rows.
 * It carries no days-left, so it cannot take part in the urgency sort at all
 * — and an unknown outranks a known shortage.
 */
export function InventoryUntrackedRow({ material: m, tz, onJumpToDelivery, onAddStock,
  canEdit,
}: IInventoryUntrackedRowProps) {
  const { t } = useTranslation();
  const { locked } = useLogisticsEditLock();
  /**
   * Three states, and only the last two are actionable.
   *
   * The material is worth counting once it should physically BE here
   * — either the order says it arrived, or its due day has come and
   * nobody has said otherwise. Before that there is nothing on site
   * to count, so the row states the gap and offers only the jump.
   */
  const arrived = m.deliveredAt != null;
  // Ticks, so the row turns actionable at the site's midnight on its due day.
  const today = zonedDay(useNow(60_000).toISOString(), tz);
  const due = untrackedDue(m, today);
  const onSite = untrackedOnSite(m, today);
  return (
    <li className={styles.row} data-untracked="true">
      <div className={styles.main}>
        <span className={styles.name}>{m.materialName}</span>
        <span className={styles.meta}>
          {m.quantity != null
            ? t('logistics.inventory.untracked.ordered', {
                amount: t('logistics.inventory.amountUnit', {
                  amount: m.quantity,
                  unit: unitLabel(t, m.unit ?? ''),
                }).trim(),
              })
            : t('logistics.inventory.untracked.orderedUnknown')}
        </span>
        {/* Where it came from, so the row is traceable to the order that
            raised it rather than appearing from nowhere. The ref alone:
            this line used to append the order's upstream workflow state,
            which was the last place that vocabulary survived on screen —
            the board's columns are the only status the product speaks. */}
        <span className={styles.provenance}>
          {t('logistics.inventory.untracked.from', { ref: m.deliveryRef })}
        </span>
        {/* The line goes to the ORDER, the same as every other line on
            this panel that names a delivery. Every untracked material
            has one by construction — it is derived from the delivery
            list — so there is no variant without somewhere to go. */}
        <span className={styles.untrackedLine}>
        <button
          type="button"
          className={styles.hint}
          // Arrived is a fact (green); a due date that has come is a
          // question nobody has answered (amber) — the same tones the
          // tracked rows use for the same two situations.
          data-tone={arrived ? 'incoming' : due ? 'reorder' : 'untracked'}
          aria-label={t('logistics.inventory.jumpToDelivery', { ref: m.deliveryRef })}
          onClick={() => onJumpToDelivery(m.deliveryRef)}
        >
          {/* A truck once the delivery is the subject — the line is
              about an arrival, not about missing data. */}
          {onSite ? <TruckGlyph /> : <InfoGlyph />}
          {t(
            arrived
              ? // The tracked row's own arrival line — same statement,
                // so the two cannot drift into different words for the
                // same fact.
                'logistics.inventory.syncAsk'
              : due
                ? 'logistics.inventory.untracked.dueReached'
                : 'logistics.inventory.untracked.prompt',
          )}
          <JumpChevron />
        </button>
        {/* Beside the note it belongs to, the way the tracked rows keep
            Sync next to their own line. Offered only once the material
            should physically be here — before that it is a to-do
            nobody can do. */}
        {onSite && canEdit ? (
          <button type="button" className={styles.sideAction} onClick={() => onAddStock(m)} disabled={locked}>
            {t('logistics.inventory.untracked.add')}
          </button>
        ) : null}
        </span>
      </div>
      <div className={styles.side}>
        {/* Dashed, like `needsCheck`: both mean "there is no figure
            here", which is a different claim from any number. */}
        <span className={styles.badge} data-tone="untracked">
          {t('logistics.inventory.untracked.badge')}
        </span>
      </div>
    </li>
  );
}
